import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { AttDay, AttMe, AttPersonMonth, AttReading, AttRequest, AttSettings, AttStatus, AttTeamDay, AttTeamMonth, CrmAuth, Office } from './crmApi';
import { Empty, Modal, Spinner, Tabs, fmtHm, fmtWhen, inputCls, labelCls, purpleBg } from './ui';

// ── Sales attendance ─────────────────────────────────────────
// A Salesperson presses Start work when they reach the office and End work
// before they leave; both work only near an office the admin set (the server
// checks the reading again). The admin and the Lead Managers hear who
// started, late or not, and who hasn't. With attendance on, a Salesperson
// gets new leads only in between. Can't press it here (no GPS, a field
// visit)? Ask, and a manager approves the time. Mirrors the app's
// crm_attendance_screens.dart; the server is routes/crmAttendance.js.

const IST = 'Asia/Kolkata';
const todayIst = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const nowHm = () => new Date().toLocaleTimeString('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false });
const timeOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase() : '');
const hmOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const addDays = (date: string, n: number) => new Date(Date.parse(`${date}T00:00:00Z`) + n * 86400e3).toISOString().slice(0, 10);
const monthOf = (date: string) => date.slice(0, 7);
const shiftMonth = (month: string, n: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', month: 'long', year: 'numeric' });
export const minsText = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`);
const metresText = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(m >= 10000 ? 0 : 1)} km` : `${m} m`);

// Metres between two points on the earth (the server works it out the same way)
function metres(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const rad = (x: number) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h))));
}
const nearestOffice = (offices: Office[], p: { lat: number; lng: number }) =>
  offices.map(o => ({ office: o, distance: metres(o, p) })).sort((a, b) => a.distance - b.distance)[0] || null;

// Where this browser is (a phone's GPS; a computer guesses from its network)
export function readLocation(): Promise<AttReading> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('This browser cannot tell where you are. Use the app.')); return; }
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy), via: 'website' }),
      e => reject(new Error(e.code === 1
        ? 'Location is blocked for this site. Allow it (the lock icon next to the address), then check again.'
        : 'Could not find where you are. Switch on location (GPS) and check again, or use the app.')),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
}

export const STATUS: Record<AttStatus, { text: string; cls: string; cell: string }> = {
  'on-time':     { text: '✅ On time',     cls: 'bg-green-100 text-green-800',   cell: 'bg-green-500 text-white' },
  late:          { text: '⏰ Late',        cls: 'bg-amber-100 text-amber-800',   cell: 'bg-amber-500 text-white' },
  'not-yet':     { text: 'Not due yet',   cls: 'bg-gray-100 text-gray-600',     cell: 'bg-gray-100 text-gray-500' },
  'not-started': { text: '❗ Not started', cls: 'bg-red-100 text-red-700',       cell: 'bg-red-200 text-red-800' },
  absent:        { text: 'Absent',        cls: 'bg-red-100 text-red-700',       cell: 'bg-red-500 text-white' },
  leave:         { text: '🌴 Leave',       cls: 'bg-sky-100 text-sky-800',       cell: 'bg-sky-400 text-white' },
  holiday:       { text: '🏖️ Holiday',     cls: 'bg-purple-100 text-purple-800', cell: 'bg-purple-200 text-purple-800' },
  off:           { text: 'Day off',       cls: 'bg-gray-100 text-gray-500',     cell: 'bg-gray-100 text-gray-400' },
  future:        { text: '',              cls: 'bg-gray-50 text-gray-400',      cell: 'bg-white text-gray-400 border border-gray-100' },
  none:          { text: '—',             cls: 'bg-gray-50 text-gray-400',      cell: 'bg-gray-50 text-gray-300' },
};

export const StatusChip: React.FC<{ status: AttStatus }> = ({ status }) => (
  STATUS[status].text ? <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS[status].cls}`}>{STATUS[status].text}</span> : null
);

const hoursText = (d: AttDay) => (d.from ? `${d.shiftName || 'Working day'} · ${fmtHm(d.from)} – ${fmtHm(d.to)}` : '');
const placeText = (p: AttDay['start']) => (!p ? '' : p.via === 'manual' ? 'set by hand' : [p.office, p.distance !== null ? `${metresText(p.distance)} away` : '', p.via === 'website' ? 'website' : ''].filter(Boolean).join(' · '));

// ── Asking for a time ────────────────────────────────────────

const AskModal: React.FC<{ auth: CrmAuth; kind?: 'start' | 'end'; onClose: () => void; onDone: () => void }> = ({ auth, kind: k0 = 'start', onClose, onDone }) => {
  const today = todayIst();
  const [date, setDate] = useState(today);
  const [kind, setKind] = useState<'start' | 'end'>(k0);
  const [time, setTime] = useState(nowHm());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!reason.trim()) { toast.error('Say why you could not press it'); return; }
    setBusy(true);
    try {
      await crm.attAsk(auth, { date, kind, time, reason: reason.trim() });
      toast.success('Sent. A Lead Manager will approve it.');
      onDone();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="Ask to mark attendance" sub="For when Start work or End work didn't work at the office" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Which one">
          {(['start', 'end'] as const).map(k => (
            <button key={k} role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
              className={`py-2.5 rounded-xl text-sm font-bold border-2 ${kind === k ? 'border-purple-500 bg-purple-50 text-purple-900' : 'border-gray-200 text-gray-600'}`}>
              {k === 'start' ? '▶ Start work' : '⏹ End work'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Day</span>
            <input type="date" value={date} max={today} min={addDays(today, -7)} onChange={e => setDate(e.target.value)} className={inputCls} />
          </label>
          <label className="block"><span className={labelCls}>Time</span>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} className={inputCls} />
          </label>
        </div>
        <label className="block"><span className={labelCls}>Why couldn't you press it?</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={300} className={inputCls}
            placeholder="GPS not working · phone switched off · field visit to …" />
        </label>
        <button onClick={send} disabled={busy} className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={purpleBg}>{busy ? 'Sending…' : 'Send to the Lead Manager'}</button>
      </div>
    </Modal>
  );
};

// ── The Start work card (the salesperson's Today page) ───────

type Where = { state: 'idle' | 'locating' | 'error' | 'ready'; error?: string; reading?: AttReading; at?: number; office?: Office; distance?: number };

export const StartWorkCard: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [me, setMe] = useState<AttMe | null>(null);
  const [where, setWhere] = useState<Where>({ state: 'idle' });
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState<'start' | 'end' | null>(null);
  const meRef = useRef<AttMe | null>(null);

  const load = useCallback(async () => {
    try { const m = await crm.attMe(auth); meRef.current = m; setMe(m); } catch { /* the page shows its own errors */ }
  }, [auth]);

  const locate = useCallback(async (): Promise<Where> => {
    setWhere(w => ({ ...w, state: 'locating', error: undefined }));
    try {
      const reading = await readLocation();
      const n = nearestOffice(meRef.current?.offices || [], reading);
      const w: Where = { state: 'ready', reading, at: Date.now(), office: n?.office, distance: n?.distance };
      setWhere(w);
      return w;
    } catch (e: any) {
      const w: Where = { state: 'error', error: e.message };
      setWhere(w);
      return w;
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const day = me?.day || null;
  const pressing = !!me?.needsStart && !!day && !day.off && !day.endAt;
  useEffect(() => { if (pressing && where.state === 'idle') locate(); }, [pressing, where.state, locate]);

  // On, from a later day: say when it starts
  if (me && me.on && me.active === false && me.pressesStart) {
    return <p className="text-xs font-semibold rounded-xl px-4 py-2.5 bg-purple-50 text-purple-900">🕘 Attendance starts on {me.sinceLabel}: press Start work when you reach the office that day.</p>;
  }
  if (!me || !me.needsStart || !day) return null;
  if (day.off) {
    if (day.off === 'off') return null;
    return <p className="text-xs font-semibold rounded-xl px-4 py-2.5 bg-sky-50 text-sky-800">{day.off === 'leave' ? '🌴 You are on leave today: no Start work needed.' : '🏖️ A holiday today: no Start work needed.'}</p>;
  }
  const inRange = where.state === 'ready' && where.distance !== undefined && where.distance <= me.range;
  const press = async (kind: 'start' | 'end') => {
    setBusy(true);
    try {
      // A reading from the last half minute will do; otherwise a fresh one
      const w = where.state === 'ready' && where.at && Date.now() - where.at < 30000 ? where : await locate();
      if (w.state !== 'ready' || !w.reading) { toast.error(w.error || 'Could not find where you are'); return; }
      const r = kind === 'start' ? await crm.attStart(auth, w.reading) : await crm.attEnd(auth, w.reading);
      toast.success(kind === 'start'
        ? (r.day.lateMinutes > 0 ? `Started at ${timeOf(r.day.startAt)}: ${minsText(r.day.lateMinutes)} late` : `Started at ${timeOf(r.day.startAt)}. Have a good day!`)
        : `Ended at ${timeOf(r.day.endAt)} · ${minsText(r.day.workedMinutes)} worked`);
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const pendingFor = (kind: 'start' | 'end') => me.pending.find(p => p.date === me.today && p.kind === kind);
  const lunch = `Lunch ${fmtHm(me.lunch.start)} – ${fmtHm(me.lunch.end)}`;
  const due = day.from ? `${hoursText(day)} · ${lunch}` : lunch;

  const placeLine = (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      {where.state === 'locating' && <span className="text-white/70">📍 Finding where you are…</span>}
      {where.state === 'error' && <span className="text-red-200">📍 {where.error}</span>}
      {where.state === 'ready' && (inRange
        ? <span className="font-semibold text-green-300">📍 {where.office?.name} · {metresText(where.distance!)} away ✅</span>
        : <span className="font-semibold text-amber-200">📍 Not at the office · {metresText(where.distance ?? 0)} from {where.office?.name || 'it'}{where.reading?.accuracy && where.reading.accuracy > 150 ? ` (rough: ±${where.reading.accuracy} m)` : ''}</span>)}
      {where.state !== 'locating' && <button onClick={() => locate()} className="text-xs font-semibold text-white/70 underline underline-offset-2">Check again</button>}
    </div>
  );

  // Ended: the day's summary
  if (day.endAt) {
    return (
      <div className="rounded-2xl px-4 py-3 bg-green-50 border border-green-100 flex items-center gap-3 flex-wrap">
        <span className="text-sm font-bold text-green-800">✅ Day done: {timeOf(day.startAt)} – {timeOf(day.endAt)}</span>
        <span className="text-xs text-green-700">{minsText(day.workedMinutes)} worked{day.lateMinutes ? ` · ${minsText(day.lateMinutes)} late` : ''}{day.earlyMinutes ? ` · left ${minsText(day.earlyMinutes)} early` : ''}{day.endAuto ? ' · End work not pressed' : ''}</span>
      </div>
    );
  }

  // Started: at work, End work before leaving
  if (day.startAt) {
    const endPending = pendingFor('end');
    return (
      <div className="rounded-2xl p-4 text-white space-y-3" style={{ background: 'linear-gradient(135deg,#14532d,#166534)' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-bold">🟢 At work since {timeOf(day.startAt)}{day.lateMinutes > 0 ? ` · ${minsText(day.lateMinutes)} late` : ' · on time'}</p>
            <p className="text-xs text-white/70 mt-0.5">{due}. Press End work before you leave.</p>
          </div>
          <button onClick={() => press('end')} disabled={busy || !inRange}
            className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-green-800 disabled:opacity-50">{busy ? 'Ending…' : '⏹ End work'}</button>
        </div>
        {placeLine}
        {endPending ? <p className="text-xs text-white/80">Waiting for approval: End work {fmtHm(endPending.time)}</p>
          : <button onClick={() => setAsking('end')} className="text-xs font-semibold text-white/70 underline underline-offset-2">Can't press it here? Ask the Lead Manager</button>}
        {asking && <AskModal auth={auth} kind={asking} onClose={() => setAsking(null)} onDone={() => { setAsking(null); load(); }} />}
      </div>
    );
  }

  // Not started yet
  const late = day.status === 'not-started';
  const startPending = pendingFor('start');
  return (
    <div className="rounded-3xl p-5 text-white space-y-4" style={{ background: late ? 'linear-gradient(135deg,#7f1d1d,#b45309)' : 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
      <div>
        <p className="text-white/60 text-xs font-bold uppercase tracking-wide">Attendance</p>
        <h2 className="text-xl font-bold mt-0.5">{late ? `You are late: your hours began at ${fmtHm(day.from)}` : 'Press Start work when you reach the office'}</h2>
        <p className="text-sm text-white/70 mt-1">{due}</p>
      </div>
      {placeLine}
      <button onClick={() => press('start')} disabled={busy || !inRange}
        className="w-full sm:w-auto px-8 py-3.5 rounded-2xl text-base font-bold text-gray-900 disabled:opacity-40 shadow-lg" style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
        {busy ? 'Starting…' : '▶ START WORK'}
      </button>
      {!inRange && where.state === 'ready' && <p className="text-xs text-white/70">The button works within {me.range} m of the office.</p>}
      {startPending ? <p className="text-xs text-white/80">Waiting for approval: Start work {fmtHm(startPending.time)}</p>
        : <button onClick={() => setAsking('start')} className="block text-xs font-semibold text-white/70 underline underline-offset-2">Can't start here (no GPS, a field visit)? Ask the Lead Manager</button>}
      {asking && <AskModal auth={auth} kind={asking} onClose={() => setAsking(null)} onDone={() => { setAsking(null); load(); }} />}
    </div>
  );
};

// ── A month as a calendar ────────────────────────────────────

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MonthGrid: React.FC<{ days: AttDay[]; onPick: (d: AttDay) => void }> = ({ days, onPick }) => {
  const lead = days.length ? (new Date(`${days[0].date}T00:00:00Z`).getUTCDay() + 6) % 7 : 0;
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">{WEEK.map(w => <p key={w} className="text-[10px] font-bold text-gray-400 text-center">{w}</p>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, i) => <div key={`x${i}`} />)}
        {days.map(d => (
          <button key={d.date} onClick={() => onPick(d)} title={`${d.label}: ${STATUS[d.status].text || 'ahead'}`}
            className={`aspect-square sm:aspect-auto sm:h-14 rounded-lg flex flex-col items-center justify-center text-[11px] font-bold ${STATUS[d.status].cell}`}>
            <span>{Number(d.date.slice(8))}</span>
            {d.startAt && <span className="hidden sm:block text-[9px] font-semibold opacity-90">{hmOf(d.startAt)}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

const Legend: React.FC = () => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500">
    {(['on-time', 'late', 'absent', 'leave', 'holiday'] as AttStatus[]).map(s => (
      <span key={s} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${STATUS[s].cell}`} />{STATUS[s].text.replace(/^\S+ /, '')}</span>
    ))}
  </div>
);

const Totals: React.FC<{ t: AttPersonMonth['totals'] }> = ({ t }) => (
  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
    {[
      ['Present', `${t.present}`], ['On time', `${t.onTime}`], ['Late', `${t.late}${t.lateMinutes ? ` · ${minsText(t.lateMinutes)}` : ''}`],
      ['Left early', `${t.early}`], ['Absent', `${t.absent}`], ['Worked', minsText(t.workedMinutes)],
    ].map(([k, v]) => (
      <div key={k} className="bg-gray-50 rounded-xl px-3 py-2">
        <p className="text-[10px] font-bold text-gray-400 uppercase">{k}</p>
        <p className={`text-sm font-bold ${k === 'Absent' && t.absent > 0 ? 'text-red-600' : k === 'Late' && t.late > 0 ? 'text-amber-700' : 'text-gray-800'}`}>{v}</p>
      </div>
    ))}
  </div>
);

const DayDetail: React.FC<{ d: AttDay; name?: string; onClose: () => void; onFix?: () => void }> = ({ d, name, onClose, onFix }) => (
  <Modal title={`${name ? `${name} · ` : ''}${d.label}`} sub={hoursText(d) || (STATUS[d.status].text || '')} onClose={onClose}>
    <div className="space-y-3 text-sm">
      <StatusChip status={d.status} />
      {d.halfDay && d.halfDay.from && <p className="text-xs text-sky-700 font-semibold">Half day of leave {fmtHm(d.halfDay.from)} – {fmtHm(d.halfDay.to)}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-[10px] font-bold text-gray-400 uppercase">Start work</p>
          <p className="font-bold text-gray-800">{d.startAt ? timeOf(d.startAt) : '—'}</p>
          <p className="text-[11px] text-gray-500">{placeText(d.start)}{d.lateMinutes ? ` · ${minsText(d.lateMinutes)} late` : ''}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-[10px] font-bold text-gray-400 uppercase">End work</p>
          <p className="font-bold text-gray-800">{d.endAt ? timeOf(d.endAt) : '—'}</p>
          <p className="text-[11px] text-gray-500">{d.endAuto ? 'Not pressed: closed at the end of the hours' : placeText(d.end)}{d.earlyMinutes ? ` · ${minsText(d.earlyMinutes)} early` : ''}</p>
        </div>
      </div>
      {d.workedMinutes > 0 && <p className="text-xs text-gray-600">Worked {minsText(d.workedMinutes)} (lunch not counted)</p>}
      {(d.setByName || d.note) && <p className="text-xs text-gray-500">{d.setByName ? `Set by ${d.setByName}` : ''}{d.note ? `${d.setByName ? ': ' : ''}${d.note}` : ''}</p>}
      {onFix && <button onClick={onFix} className="w-full py-2.5 rounded-xl text-sm font-bold border-2 border-purple-200 text-purple-700 hover:bg-purple-50">✏️ Set the times</button>}
    </div>
  </Modal>
);

const RequestChip: React.FC<{ r: AttRequest }> = ({ r }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.status === 'Pending' ? 'bg-amber-100 text-amber-800' : r.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
    {r.status === 'Pending' ? 'Waiting for approval' : r.status === 'Approved' ? 'Approved' : 'Not approved'}
  </span>
);

const MonthNav: React.FC<{ month: string; onMonth: (m: string) => void; max: string }> = ({ month, onMonth, max }) => (
  <div className="flex items-center gap-2">
    <button onClick={() => onMonth(shiftMonth(month, -1))} className="w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" aria-label="Month before">‹</button>
    <p className="font-bold text-gray-800 min-w-[130px] text-center">{monthLabel(month)}</p>
    <button onClick={() => onMonth(shiftMonth(month, 1))} disabled={month >= max} className="w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30" aria-label="Next month">›</button>
  </div>
);

// ── A salesperson's own month (Shift & Requests) ─────────────

export const CrmMyAttendance: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [month, setMonth] = useState(monthOf(todayIst()));
  const [data, setData] = useState<AttPersonMonth | null>(null);
  const [requests, setRequests] = useState<AttRequest[]>([]);
  const [pick, setPick] = useState<AttDay | null>(null);
  const [asking, setAsking] = useState(false);
  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([crm.attMonth(auth, month), crm.attRequests(auth)]);
      setData(m);
      setRequests(r.requests);
    } catch (e: any) { toast.error(e.message); }
  }, [auth, month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner label="Loading your attendance…" />;
  if (!data.on) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-bold text-gray-800">🕘 My attendance</p>
          <p className="text-xs text-gray-500">Start work and End work at the office. Tap a day to see it.</p>
        </div>
        <MonthNav month={month} onMonth={setMonth} max={monthOf(data.today)} />
      </div>
      <Totals t={data.totals} />
      <MonthGrid days={data.days} onPick={setPick} />
      <Legend />
      <div className="flex items-center justify-between gap-3 flex-wrap border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-500">Missed pressing it at the office? Ask, and a Lead Manager sets the time.</p>
        <button onClick={() => setAsking(true)} className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={purpleBg}>📝 Ask to mark attendance</button>
      </div>
      {requests.length > 0 && (
        <div className="space-y-2">
          {requests.slice(0, 10).map(r => (
            <div key={r.id} className="flex items-start gap-2 flex-wrap text-xs border border-gray-100 rounded-xl px-3 py-2">
              <span className="font-bold text-gray-700">{r.dateLabel} · {r.kind === 'start' ? 'Start work' : 'End work'} {fmtHm(r.time)}</span>
              <RequestChip r={r} />
              <span className="text-gray-500 w-full">“{r.reason}”{r.decidedByName ? ` · ${r.status === 'Approved' ? 'approved' : 'turned down'} by ${r.decidedByName}` : ''}{r.note ? `: ${r.note}` : ''}</span>
            </div>
          ))}
        </div>
      )}
      {pick && <DayDetail d={pick} onClose={() => setPick(null)} />}
      {asking && <AskModal auth={auth} onClose={() => setAsking(false)} onDone={() => { setAsking(false); load(); }} />}
    </div>
  );
};

// ── Managers: set or clear a day's times ─────────────────────

const FixModal: React.FC<{ auth: CrmAuth; person: { id: string; name: string }; day: AttDay; onClose: () => void; onDone: () => void }> = ({ auth, person, day, onClose, onDone }) => {
  const [start, setStart] = useState(hmOf(day.startAt) || day.from || '');
  const [end, setEnd] = useState(hmOf(day.endAt));
  const [note, setNote] = useState(day.note || '');
  const [busy, setBusy] = useState(false);
  const save = async (clear = false) => {
    if (clear && !window.confirm(`Clear ${person.name}'s Start work and End work on ${day.label}?`)) return;
    setBusy(true);
    try {
      await crm.attFix(auth, { salesPersonId: person.id, date: day.date, start, end, note: note.trim(), clear });
      toast.success(clear ? 'Cleared' : 'Saved');
      onDone();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${person.name} · ${day.label}`} sub={hoursText(day) || 'Set the times by hand'} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Start work</span><input type="time" value={start} onChange={e => setStart(e.target.value)} className={inputCls} /></label>
          <label className="block"><span className={labelCls}>End work (empty: none)</span><input type="time" value={end} onChange={e => setEnd(e.target.value)} className={inputCls} /></label>
        </div>
        <label className="block"><span className={labelCls}>Note</span>
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={300} className={inputCls} placeholder="Why it was set by hand" />
        </label>
        <p className="text-[11px] text-gray-500">Late and early are worked out again from these times.</p>
        <div className="flex gap-2">
          <button onClick={() => save(false)} disabled={busy || !start} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={purpleBg}>{busy ? 'Saving…' : 'Save'}</button>
          {day.startAt && <button onClick={() => save(true)} disabled={busy} className="px-4 py-2.5 rounded-xl text-sm font-bold border-2 border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Clear the day</button>}
        </div>
      </div>
    </Modal>
  );
};

// ── Managers: the team's day ─────────────────────────────────

const DayView: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [date, setDate] = useState('');
  const [data, setData] = useState<AttTeamDay | null>(null);
  const [fix, setFix] = useState<(AttDay & { id: string; name: string }) | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.attDay(auth, date)); } catch (e: any) { toast.error(e.message); }
  }, [auth, date]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);
  if (!data) return <Spinner label="Loading the day…" />;
  const tiles: [string, number, string][] = [
    ['On time', data.counts['on-time'] || 0, 'text-green-700'], ['Late', data.counts.late || 0, 'text-amber-700'],
    ['Not started', data.counts['not-started'] || 0, 'text-red-600'], ['Not due yet', data.counts['not-yet'] || 0, 'text-gray-600'],
    ['Absent', data.counts.absent || 0, 'text-red-600'], ['Leave', data.counts.leave || 0, 'text-sky-700'],
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={date || data.date} max={data.today} onChange={e => setDate(e.target.value)} className={`${inputCls} max-w-[180px]`} aria-label="Day" />
        {data.date !== data.today && <button onClick={() => setDate('')} className="px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-600">Today</button>}
        <button onClick={load} className="px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-600">↻ Refresh</button>
        {!data.working && <span className="text-xs font-bold text-gray-500">{data.label} is not a working day</span>}
        {data.active === false && <span className="text-xs font-bold text-purple-800 bg-purple-50 rounded-lg px-2.5 py-1">Attendance starts on {data.sinceLabel}</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {tiles.map(([k, n, cls]) => (
          <div key={k} className="bg-white border border-gray-100 rounded-xl px-3 py-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase">{k}</p>
            <p className={`text-xl font-bold tabular-nums ${n ? cls : 'text-gray-300'}`}>{n}</p>
          </div>
        ))}
      </div>
      {!data.people.length ? <Empty icon="👥" title="No salespeople" sub="Lead Managers and Sales Admins don't press Start work" /> : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {data.people.map(p => (
            <div key={p.id} className="px-4 py-3 flex items-start gap-3 flex-wrap">
              <div className="min-w-[150px] flex-1">
                <p className="font-bold text-gray-800">{p.name}</p>
                <p className="text-[11px] text-gray-400">{hoursText(p) || (p.off === 'leave' ? 'On leave' : p.off === 'holiday' ? 'Holiday' : 'Not a working day')}</p>
                {p.halfDay?.from && <p className="text-[10px] text-sky-700 font-semibold">Half day {fmtHm(p.halfDay.from)} – {fmtHm(p.halfDay.to)}</p>}
              </div>
              <div className="min-w-[110px]"><StatusChip status={p.status} /></div>
              <div className="min-w-[150px] text-xs">
                <p className="font-semibold text-gray-700">{p.startAt ? `▶ ${timeOf(p.startAt)}` : '▶ —'}{p.lateMinutes ? <span className="text-amber-700"> · {minsText(p.lateMinutes)} late</span> : null}</p>
                {p.start && <p className="text-[10px] text-gray-400">{placeText(p.start)}</p>}
              </div>
              <div className="min-w-[150px] text-xs">
                <p className="font-semibold text-gray-700">{p.endAt ? `⏹ ${timeOf(p.endAt)}` : p.startAt ? '⏹ at work' : '⏹ —'}{p.earlyMinutes ? <span className="text-amber-700"> · {minsText(p.earlyMinutes)} early</span> : null}</p>
                <p className="text-[10px] text-gray-400">{p.endAuto ? 'End work not pressed' : p.workedMinutes ? `${minsText(p.workedMinutes)} worked` : placeText(p.end)}</p>
              </div>
              {!p.off && p.date <= data.today && (
                <button onClick={() => setFix(p)} className="text-xs font-bold px-3 py-1.5 rounded-lg border-2 border-gray-200 text-gray-600 hover:bg-gray-50">✏️ Set times</button>
              )}
            </div>
          ))}
        </div>
      )}
      {fix && <FixModal auth={auth} person={{ id: fix.id, name: fix.name }} day={fix} onClose={() => setFix(null)} onDone={() => { setFix(null); load(); }} />}
    </div>
  );
};

// ── Managers: the month ──────────────────────────────────────

const PersonMonthModal: React.FC<{ auth: CrmAuth; person: { id: string; name: string }; month: string; onClose: () => void; onChanged: () => void }> = ({ auth, person, month: m0, onClose, onChanged }) => {
  const [month, setMonth] = useState(m0);
  const [data, setData] = useState<AttPersonMonth | null>(null);
  const [pick, setPick] = useState<AttDay | null>(null);
  const [fix, setFix] = useState<AttDay | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.attPersonMonth(auth, month, person.id)); } catch (e: any) { toast.error(e.message); }
  }, [auth, month, person.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <Modal title={`${person.name} · attendance`} sub="Tap a day to see it or set its times" onClose={onClose} wide>
      {!data ? <Spinner /> : (
        <div className="space-y-4">
          <MonthNav month={month} onMonth={setMonth} max={monthOf(data.today)} />
          <Totals t={data.totals} />
          <MonthGrid days={data.days} onPick={setPick} />
          <Legend />
          {data.totals.absentDates.length > 0 && <p className="text-xs text-red-700">Absent: {data.totals.absentDates.map(d => `${d.label}${d.days < 1 ? ' (half)' : ''}`).join(', ')}</p>}
        </div>
      )}
      {pick && <DayDetail d={pick} name={person.name} onClose={() => setPick(null)}
        onFix={!pick.off && data && pick.date <= data.today ? () => { setFix(pick); setPick(null); } : undefined} />}
      {fix && <FixModal auth={auth} person={person} day={fix} onClose={() => setFix(null)} onDone={() => { setFix(null); load(); onChanged(); }} />}
    </Modal>
  );
};

const MonthView: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [month, setMonth] = useState(monthOf(todayIst()));
  const [data, setData] = useState<AttTeamMonth | null>(null);
  const [open, setOpen] = useState<{ id: string; name: string } | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.attTeamMonth(auth, month)); } catch (e: any) { toast.error(e.message); }
  }, [auth, month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner label="Loading the month…" />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <MonthNav month={month} onMonth={setMonth} max={monthOf(data.today)} />
        <Legend />
      </div>
      {!data.people.length ? <Empty icon="👥" title="No salespeople" /> : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-gray-50"><tr>
              {['Person', 'Present', 'Late', 'Left early', 'No end', 'Absent', 'Leave', 'Worked', 'Each day'].map(h => (
                <th key={h} className={`px-3 py-3 text-xs font-bold text-gray-500 ${h === 'Person' || h === 'Each day' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.people.map(p => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-purple-50/40 cursor-pointer" onClick={() => setOpen({ id: p.id, name: p.name })}>
                  <td className="px-3 py-3 font-bold text-gray-800 whitespace-nowrap">{p.name}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{p.totals.present}</td>
                  <td className={`px-3 py-3 text-right tabular-nums ${p.totals.late ? 'text-amber-700 font-bold' : ''}`}>{p.totals.late}{p.totals.lateMinutes ? <span className="block text-[10px] font-semibold">{minsText(p.totals.lateMinutes)}</span> : null}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{p.totals.early}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{p.totals.noEnd}</td>
                  <td className={`px-3 py-3 text-right tabular-nums ${p.totals.absent ? 'text-red-600 font-bold' : ''}`}>{p.totals.absent}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{p.totals.leave}</td>
                  <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">{minsText(p.totals.workedMinutes)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-[2px]" aria-label={`${p.name}'s days`}>
                      {p.marks.map((s, i) => <span key={i} title={`${data.dates[i]}: ${STATUS[s].text || 'ahead'}`} className={`w-2 h-4 rounded-[2px] ${STATUS[s].cell}`} />)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <PersonMonthModal auth={auth} person={open} month={month} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

// ── The offices and rules (the admin changes them) ───────────

// "13.0827, 80.2707" as Google Maps copies it (or a maps link with @lat,lng)
function parseLatLng(text: string): { lat: number; lng: number } | null {
  const m = text.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (!m) return null;
  const lat = Number(m[1]), lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

const SettingsView: React.FC<{ auth: CrmAuth; isAdmin: boolean; onSaved?: () => void }> = ({ auth, isAdmin, onSaved }) => {
  const [s, setS] = useState<AttSettings | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [range, setRange] = useState('100');
  const [grace, setGrace] = useState('10');
  const [lunch, setLunch] = useState({ start: '13:15', end: '14:00' });
  const [name, setName] = useState('');
  const [where, setWhere] = useState('');
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const fill = (d: AttSettings) => {
    setS(d); setOffices(d.offices); setRange(String(d.range)); setGrace(String(d.grace)); setLunch(d.lunch);
  };
  useEffect(() => { crm.attSettings(auth).then(fill).catch((e: any) => toast.error(e.message)); }, [auth]);
  if (!s) return <Spinner />;

  const save = async (next: Office[] = offices) => {
    setBusy(true);
    try {
      const d = await crm.attSaveSettings(auth, { offices: next, range: Number(range), grace: Number(grace), lunch });
      fill(d);
      toast.success(d.on ? 'Saved. Attendance is on.' : 'Saved. Attendance is off (no office).');
      onSaved?.();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const here = async () => {
    setLocating(true);
    try {
      const r = await readLocation();
      setWhere(`${r.lat.toFixed(6)}, ${r.lng.toFixed(6)}`);
      if ((r.accuracy || 0) > 100) toast.info(`That reading is rough (±${r.accuracy} m). On a phone with GPS it is closer; or paste the place from Google Maps.`);
    } catch (e: any) { toast.error(e.message); } finally { setLocating(false); }
  };
  const addOffice = () => {
    const p = parseLatLng(where);
    if (!name.trim()) { toast.error('Give the office a name'); return; }
    if (!p) { toast.error('Paste the location like 13.0827, 80.2707, or use "My location here"'); return; }
    const next = [...offices, { id: '', name: name.trim(), ...p }];
    setName(''); setWhere('');
    save(next);
  };

  return (
    <div className="space-y-4">
      {!s.on && <p className="text-sm font-semibold rounded-xl px-4 py-3 bg-amber-50 text-amber-900">Attendance is off until an office is added. Add it once the salespeople have the new app (or use the website), because from then on a Salesperson gets new leads only after Start work.</p>}
      {!isAdmin && <p className="text-xs text-gray-500">Only the admin changes these.</p>}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <p className="font-bold text-gray-800">🏢 Offices</p>
        {!offices.length ? <p className="text-xs text-gray-500">No office yet.</p> : offices.map(o => (
          <div key={o.id || o.name} className="flex items-center gap-3 flex-wrap border border-gray-100 rounded-xl px-3 py-2">
            <span className="font-semibold text-gray-800">{o.name}</span>
            <a href={`https://www.google.com/maps?q=${o.lat},${o.lng}`} target="_blank" rel="noreferrer" className="text-xs text-purple-700 underline">{o.lat.toFixed(5)}, {o.lng.toFixed(5)} ↗</a>
            {isAdmin && <button onClick={() => window.confirm(`Remove ${o.name}?`) && save(offices.filter(x => x !== o))} disabled={busy} className="ml-auto text-xs font-bold text-red-600 hover:underline">Remove</button>}
          </div>
        ))}
        {isAdmin && (
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <p className={labelCls}>Add an office</p>
            <div className="grid sm:grid-cols-[1fr_1.4fr_auto_auto] gap-2">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (Main office)" maxLength={40} className={inputCls} aria-label="Office name" />
              <input value={where} onChange={e => setWhere(e.target.value)} placeholder="13.0827, 80.2707 (from Google Maps)" className={inputCls} aria-label="Office location" />
              <button onClick={here} disabled={locating} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-purple-200 text-purple-700 whitespace-nowrap disabled:opacity-50">{locating ? 'Finding…' : '📍 My location here'}</button>
              <button onClick={addOffice} disabled={busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>Add</button>
            </div>
            <p className="text-[11px] text-gray-400">Best: stand inside the office with a phone and press "My location here". Or, in Google Maps, press and hold on the office and copy the numbers it shows.</p>
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 grid sm:grid-cols-4 gap-3">
        <label className="block"><span className={labelCls}>Works within (m)</span><input type="number" min={30} max={1000} value={range} onChange={e => setRange(e.target.value)} disabled={!isAdmin} className={inputCls} /></label>
        <label className="block"><span className={labelCls}>Late after (min)</span><input type="number" min={0} max={60} value={grace} onChange={e => setGrace(e.target.value)} disabled={!isAdmin} className={inputCls} /></label>
        <label className="block"><span className={labelCls}>Lunch from</span><input type="time" value={lunch.start} onChange={e => setLunch({ ...lunch, start: e.target.value })} disabled={!isAdmin} className={inputCls} /></label>
        <label className="block"><span className={labelCls}>Lunch to</span><input type="time" value={lunch.end} onChange={e => setLunch({ ...lunch, end: e.target.value })} disabled={!isAdmin} className={inputCls} /></label>
        <p className="sm:col-span-4 text-[11px] text-gray-500">Late: Start work more than this many minutes after their hours begin (their shift). The same minutes before the end count as leaving early. Lunch isn't counted as time worked.</p>
        {isAdmin && <div className="sm:col-span-4"><button onClick={() => save()} disabled={busy} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={purpleBg}>{busy ? 'Saving…' : 'Save the rules'}</button></div>}
      </div>
    </div>
  );
};

// The managers' Attendance tab (Team & Shifts): the day, the month, the offices
export const CrmAttendanceManager: React.FC<{ auth: CrmAuth; isAdmin: boolean }> = ({ auth, isAdmin }) => {
  const [view, setView] = useState('day');
  const [on, setOn] = useState<boolean | null>(null);
  const check = useCallback(() => { crm.attSettings(auth).then(s => setOn(s.on)).catch(() => setOn(true)); }, [auth]);
  useEffect(() => { check(); }, [check]);
  if (on === null) return <Spinner />;
  if (!on) return <SettingsView auth={auth} isAdmin={isAdmin} onSaved={check} />;
  return (
    <div className="space-y-3">
      <Tabs label="Attendance" value={view} onChange={setView} tabs={[{ id: 'day', label: 'Day' }, { id: 'month', label: 'Month' }, { id: 'offices', label: 'Offices & rules' }]} />
      {view === 'day' && <DayView auth={auth} />}
      {view === 'month' && <MonthView auth={auth} />}
      {view === 'offices' && <SettingsView auth={auth} isAdmin={isAdmin} onSaved={check} />}
    </div>
  );
};

// ── Managers: the requests (Requests page) ───────────────────

export const CrmAttendanceRequests: React.FC<{ auth: CrmAuth; onChanged?: () => void }> = ({ auth, onChanged }) => {
  const [status, setStatus] = useState('Pending');
  const [data, setData] = useState<{ requests: AttRequest[]; counts?: { Pending: number; Approved: number; Rejected: number } } | null>(null);
  const [busy, setBusy] = useState('');
  const [turning, setTurning] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const load = useCallback(async () => {
    try { setData(await crm.attRequests(auth, status)); } catch (e: any) { toast.error(e.message); }
  }, [auth, status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (r: AttRequest, approve: boolean) => {
    setBusy(r.id);
    try {
      await crm.attDecide(auth, r.id, approve, note.trim());
      toast.success(approve ? `Marked: ${r.byName}'s ${r.kind === 'start' ? 'Start work' : 'End work'} ${fmtHm(r.time)}` : 'Turned down');
      setTurning(null); setNote('');
      load(); onChanged?.();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(''); }
  };
  return (
    <div className="space-y-3">
      <Tabs label="Status" value={status} onChange={setStatus} tabs={[
        { id: 'Pending', label: 'Waiting', badge: data?.counts?.Pending }, { id: 'Approved', label: 'Approved' }, { id: 'Rejected', label: 'Turned down' },
      ]} />
      {!data ? <Spinner /> : !data.requests.length ? <Empty icon="🕘" title={status === 'Pending' ? 'No attendance requests waiting' : 'None'} sub="A salesperson asks when Start work or End work didn't work at the office" /> : data.requests.map(r => (
        <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-800">{r.byName}</span>
            <span className="text-sm text-gray-700">{r.dateLabel}: <b>{r.kind === 'start' ? 'Start work' : 'End work'} {fmtHm(r.time)}</b></span>
            {status !== 'Pending' && <RequestChip r={r} />}
          </div>
          <p className="text-xs text-gray-600">“{r.reason}”</p>
          <p className="text-[11px] text-gray-400">Asked {fmtWhen(r.createdAt)}{r.decidedByName ? ` · ${r.status === 'Approved' ? 'approved' : 'turned down'} by ${r.decidedByName}` : ''}{r.note ? `: ${r.note}` : ''}</p>
          {r.status === 'Pending' && (turning === r.id ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why not (they will see this)" className={inputCls} autoFocus aria-label="Why not" />
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => decide(r, false)} disabled={busy === r.id} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{busy === r.id ? 'Saving…' : 'Turn down'}</button>
                <button onClick={() => { setTurning(null); setNote(''); }} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => decide(r, true)} disabled={busy === r.id} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>{busy === r.id ? 'Approving…' : `Approve: ${fmtHm(r.time)}`}</button>
              <button onClick={() => { setTurning(r.id); setNote(''); }} disabled={busy === r.id} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Turn down</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
